/**
 * Centralized API Service
 * 
 * This service provides a single abstraction layer for all API calls in the application.
 * It wraps the enhanced HTTP service and provides consistent response h    async get(endpoint, opti    async po    async put(endpoint, data, options = {}) {
        try {
            const headersWithFingerprint = this.getHeadersWithFingerprint(options.headers);
            const response = await this.httpService.put(endpoint, data, {
                ...options,
                headers: headersWithFingerprint
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }nt, data, options = {}) {
        try {
            const headersWithFingerprint = this.getHeadersWithFingerprint(options.headers);
            const response = await this.httpService.post(endpoint, data, {
                ...options,
                headers: headersWithFingerprint
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    } {
        try {
            const headersWithFingerprint = this.getHeadersWithFingerprint(options.headers);
            const response = await this.httpService.get(endpoint, {
                ...options,
                headers: headersWithFingerprint
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('An error occurred', error);
            throw error;
        }
    } * error management, and authentication across all services.
 */

import { HttpService } from './http.service.js';
import { getEndpoint } from './endpoint.js';
import authStorageService from '../auth/auth-storage.service.js';
import deviceFingerprint from '../../utils/device-fingerprint.js';
import InputSanitizer from '../../utils/input-sanitizer.js';

class ApiService {
    constructor() {
        this.httpService = new HttpService();
        this.baseURL = getEndpoint();
        this.httpService.setBaseURL(this.baseURL);
    }

    /**
     * Get enhanced headers with device fingerprinting
     * @param {Object} additionalHeaders - Additional headers to include
     * @returns {Object} Headers object with device fingerprint
     */
    getEnhancedHeaders(additionalHeaders = {}) {
        const deviceFingerprintHash = deviceFingerprint.getCurrentFingerprint();
        
        return {
            'X-Device-Fingerprint': deviceFingerprintHash,
            'X-Request-Source': 'webapp',
            'X-Client-Version': process.env.VUE_APP_VERSION || '1.0.0',
            ...additionalHeaders
        };
    }

    /**
     * Handles API response consistently across all services
     * @param {Response} response - Fetch response object
     * @returns {Promise} Parsed response data or error
     */
    async handleResponse(response) {
        let errorMsg = '';
        
        try {
            const text = await response.text();
            const data = text && JSON.parse(text);
            
            if (!response.ok) {
                if (response.status === 401 || response.status === 403) {
                    // Auto logout if 401/403 response returned from api
                    this.logout();
                    
                    // Emit session expired event instead of reloading
                    if (typeof window !== 'undefined') {
                        const event = new CustomEvent('sessionExpired', {
                            detail: { 
                                message: 'Your session has expired. Please sign in again.',
                                timestamp: Date.now()
                            }
                        });
                        window.dispatchEvent(event);
                    }
                    
                    errorMsg = 'Your session has expired. Please sign in again.';
                } else {
                    if (data && data.message) {
                        errorMsg = data.message;
                    } else if (data && data.error_message) {
                        errorMsg = data.error_message;
                    } else {
                        errorMsg = 'An unexpected error occurred.';
                    }
                }
                
                return Promise.reject(errorMsg);
            }
            
            // Check if the response has a FAILED status (API level failure)
            if (data && data.status === "FAILED") {
                errorMsg = data.message || 'Request failed';
                return Promise.reject(errorMsg);
            }
            
            return data;
        } catch (parseError) {
            console.error('Error parsing response:', parseError);
            return Promise.reject('Invalid response format');
        }
    }

    /**
     * Get headers with device fingerprinting
     * @param {Object} additionalHeaders - Additional headers to include
     * @returns {Object} Headers with device fingerprint
     */
    getHeadersWithFingerprint(additionalHeaders = {}) {
        return {
            'X-Device-Fingerprint': deviceFingerprint.getCurrentFingerprint(),
            ...additionalHeaders
        };
    }

    /**
     * Sanitize data before sending to API
     * @param {any} data - Data to sanitize
     * @returns {any} Sanitized data
     */
    sanitizeData(data) {
        return InputSanitizer.sanitizeForAPI(data);
    }
    /**
     * Logout helper function
     */
    logout() {
        authStorageService.clearAuthData();
        // Additional logout logic can be added here
    }

    /**
     * Make a GET request
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed response data
     */
    async get(endpoint, options = {}) {
        try {
            // Enhance options with device fingerprinting headers
            const enhancedOptions = {
                ...options,
                headers: {
                    ...this.getEnhancedHeaders(),
                    ...options.headers
                }
            };
            
            const response = await this.httpService.get(endpoint, enhancedOptions);
            return await this.handleResponse(response);
        } catch (error) {
            console.log('An error occurred', error);
            throw error;
        }
    }

    /**
     * Make a POST request
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed response data
     */
    async post(endpoint, data, options = {}) {
        try {
            // Enhance options with device fingerprinting headers
            const enhancedOptions = {
                ...options,
                headers: {
                    ...this.getEnhancedHeaders(),
                    ...options.headers
                }
            };
            
            const response = await this.httpService.post(endpoint, data, enhancedOptions);
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    /**
     * Make a PUT request
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed response data
     */
    async put(endpoint, data, options = {}) {
        try {
            // Enhance options with device fingerprinting headers
            const enhancedOptions = {
                ...options,
                headers: {
                    ...this.getEnhancedHeaders(),
                    ...options.headers
                }
            };
            
            const response = await this.httpService.put(endpoint, data, enhancedOptions);
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    /**
     * Make a DELETE request
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed response data
     */
    async delete(endpoint, options = {}) {
        try {
            const headersWithFingerprint = this.getHeadersWithFingerprint(options.headers);
            const response = await this.httpService.delete(endpoint, {
                ...options,
                headers: headersWithFingerprint
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    /**
     * Make a PATCH request
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {any} data - Request body data
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed response data
     */
    async patch(endpoint, data, options = {}) {
        try {
            const headersWithFingerprint = this.getHeadersWithFingerprint(options.headers);
            const response = await this.httpService.patch(endpoint, data, {
                ...options,
                headers: headersWithFingerprint
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    /**
     * Upload files
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {FormData} formData - Form data with files
     * @param {Object} options - Additional options
     * @returns {Promise} Parsed response data
     */
    async uploadFiles(endpoint, formData, options = {}) {
        try {
            const headersWithFingerprint = this.getHeadersWithFingerprint(options.headers);
            const response = await this.httpService.uploadFiles(endpoint, formData, {
                ...options,
                headers: headersWithFingerprint
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }

    /**
     * Make a request with custom headers (for legacy compatibility)
     * @param {string} endpoint - API endpoint (relative to base URL)
     * @param {Object} requestOptions - Fetch request options
     * @returns {Promise} Parsed response data
     */
    async customRequest(endpoint, requestOptions = {}) {
        try {
            // Convert to full URL for direct fetch call if needed
            const fullURL = this.baseURL + endpoint;
            const headersWithFingerprint = this.getHeadersWithFingerprint(requestOptions.headers);
            const response = await fetch(fullURL, {
                redirect: 'follow',
                ...requestOptions,
                headers: {
                    ...this.httpService.getAuthHeaders(),
                    ...headersWithFingerprint
                }
            });
            return await this.handleResponse(response);
        } catch (error) {
            console.log('error', error);
            throw error;
        }
    }
}

// Create and export singleton instance
const apiService = new ApiService();

export default apiService;
